import sum from 'lodash/sum';
import uniqBy from 'lodash/uniqBy';
import { createSlice } from '@reduxjs/toolkit';
// utils
import { selectFrom, selectOne, insertInto, updateTable, callRPC } from '../../services/databaseService';
import { CategoryState, Category, CategoryType, CategorySelect } from '../../@types/category';
import { collection, query, where, getDocs } from 'firebase/firestore';
//
import { dispatch } from '../store';

// ----------------------------------------------------------------------

const initialState: CategoryState = {
  isLoading: false,
  error: null,
  categories: [],
  category: null,
  categoriesSelect: [],
  sortBy: null,
  filters: {
    gender: [],
    category: 'All',
    colors: [],
    priceRange: [0, 200],
    rating: '',
  }
};

const slice = createSlice({
  name: 'category',
  initialState,
  reducers: {
    // START LOADING
    startLoading(state) {
      state.isLoading = true;
    },

    // HAS ERROR
    hasError(state, action) {
      state.isLoading = false;
      state.error = action.payload;
    },

    // GET CATEGORIES
    getCategoriesSuccess(state, action) {
      state.isLoading = false;
      state.categories = Array.isArray(action.payload) ? action.payload : action.payload.categories || [];
    },

    // GET SHORT CATEGORIES
    getShortCategoriesSuccess(state, action) {
      state.isLoading = false;
      state.categoriesSelect = action.payload;
    },

    // GET CATEGORY
    getCategorySuccess(state, action) {
      state.isLoading = false;
      state.category = action.payload;
    },

    //  SORT & FILTER CATEGORIES
    sortByCategories(state, action) {
      state.sortBy = action.payload;
    },

    filterCategories(state, action) {
      state.filters.gender = action.payload.gender;
      state.filters.category = action.payload.category;
      state.filters.colors = action.payload.colors;
      state.filters.priceRange = action.payload.priceRange;
      state.filters.rating = action.payload.rating;
    },
  },
});

// Reducer
export default slice.reducer;

// Actions
export const {
  sortByCategories,
  filterCategories,
} = slice.actions;

// ----------------------------------------------------------------------

export function getCategories() {
  return async () => {
    dispatch(slice.actions.startLoading());
    try {
      // Use RPC function to get products with categories, then extract unique categories
      const products = await callRPC<any[]>('get_products_with_categories');

      // Extract unique categories from products and count products per category
      const categoryMap = new Map<number, any>();
      
      products.forEach((product: any) => {
        if (product.category_id) {
          const categoryId = product.category_id;
          if (!categoryMap.has(categoryId)) {
            categoryMap.set(categoryId, {
              id: categoryId,
              name: product.category,
              description: null, // Not in products, would need separate query
              type: product.type,
              photo_url: null, // Not in products, would need separate query
              created_at: product.created_at,
              updated_at: product.updated_at,
              created_by: product.created_by,
              updated_by: product.updated_by,
              product_count: 0,
            });
          }
          // Increment product count
          const category = categoryMap.get(categoryId);
          if (category) {
            category.product_count += 1;
          }
        }
      });

      // Convert map to array and sort by name
      const categories = Array.from(categoryMap.values()).sort((a, b) => 
        a.name.localeCompare(b.name)
      );

      dispatch(slice.actions.getCategoriesSuccess(categories));
    } catch (error: any) {
      console.error('Error fetching categories:', error);
      dispatch(slice.actions.hasError(error));
    }
  };
}

// ----------------------------------------------------------------------

export function getShortCategories() {
  return async () => {
    dispatch(slice.actions.startLoading());
    try {
      // Use RPC function to get products with categories, then extract unique categories grouped by type
      const products = await callRPC<any[]>('get_products_with_categories');

      // Group categories by type
      const typeMap = new Map<string, any[]>();
      
      products.forEach((product: any) => {
        if (product.category_id && product.type) {
          const type = product.type;
          if (!typeMap.has(type)) {
            typeMap.set(type, []);
          }
          
          const categoriesForType = typeMap.get(type)!;
          // Check if category already exists in this type
          const existingCategory = categoriesForType.find((cat: any) => cat.id === product.category_id);
          if (!existingCategory) {
            categoriesForType.push({
              id: product.category_id,
              name: product.category,
            });
          }
        }
      });

      // Convert map to array format: [{ type, category: [{id, name}] }]
      const categoriesSelect = Array.from(typeMap.entries()).map(([type, category]) => ({
        type,
        category: category.sort((a, b) => a.name.localeCompare(b.name)),
      }));

      dispatch(slice.actions.getShortCategoriesSuccess(categoriesSelect));
    } catch (error: any) {
      console.error('Error fetching short categories:', error);
      dispatch(slice.actions.hasError(error));
    }
  };
}

// ----------------------------------------------------------------------

export function getCategory(id: string) {
  return async () => {
    dispatch(slice.actions.startLoading());
    try {
      const category = await selectOne<any>('product_category', { id: parseInt(id) });

      if (!category) {
        throw new Error('Category not found');
      }

      dispatch(slice.actions.getCategorySuccess(category));
      return category;
    } catch (error: any) {
      console.error('Error fetching category:', error);
      dispatch(slice.actions.hasError(error));
      throw error;
    }
  };
}

// ----------------------------------------------------------------------

export function createCategory(data: Category) {
  return async () => {
    dispatch(slice.actions.startLoading());
    try {
      // Use secure RPC function (uses auth.uid() internally)
      const category = await callRPC<any>('create_product_category', {
        p_name: data.name,
        p_type: data.type,
        p_description: data.description || null,
        p_photo_url: data.photo_url || null,
      });

      dispatch(slice.actions.getCategorySuccess(category));
      return category;
    } catch (error: any) {
      console.error('Error creating category:', error);
      dispatch(slice.actions.hasError(error));
      throw error;
    }
  };
}

// ----------------------------------------------------------------------

export function updateCategory(data: Category) {
  return async () => {
    dispatch(slice.actions.startLoading());
    try {
      // Use secure RPC function (uses auth.uid() internally)
      const category = await callRPC<any>('update_product_category', {
        p_category_id: data.id,
        p_name: data.name || undefined,
        p_type: data.type || undefined,
        p_description: data.description !== undefined ? data.description : undefined,
        p_photo_url: data.photo_url || undefined,
      });

      dispatch(slice.actions.getCategorySuccess(category));
      return category;
    } catch (error: any) {
      console.error('Error updating category:', error);
      dispatch(slice.actions.hasError(error));
      throw error;
    }
  };
}